/* eslint-disable @typescript-eslint/ban-ts-comment */
import { ApiConfig } from './api-config.model';
import { MicroserviceId } from './microservices.const';

export class BackendConfig {
    api: ApiConfig[];
    baseUrl: string | Record<MicroserviceId, string>;
    requestHeaders: Record<string, string> | Record<MicroserviceId, Record<string, string>>;
    timeout?: number;

    constructor(backend: Partial<BackendConfig>) {
        this.api = (backend.api as ApiConfig[]).map((a: ApiConfig) => new ApiConfig(a));
        this.baseUrl = backend.baseUrl || '';
        this.requestHeaders = backend.requestHeaders || {};
        this.timeout = backend.timeout || 3000;
    }

    getApiConfig(apiName: string): ApiConfig | null {
        let api;
        try {
            // eslint-disable-next-line @typescript-eslint/no-shadow
            api = this.api.find((api: ApiConfig) => api.name === apiName);
            if (api) {
                /* @ts-ignore */
                api.url = this.prepareUrl(api.url, api.service);

                // Add service-specific headers
                const serviceHeaders = this.getRequestHeaders(api.service);
                if (serviceHeaders && Object.keys(serviceHeaders).length > 0) {
                    /* @ts-ignore */
                    for (const key in serviceHeaders) {
                        if (serviceHeaders[key]) {
                            /* @ts-ignore */
                            api.headers = api.headers.set(key, serviceHeaders[key].toString());
                        }
                    }
                }
            }
        } catch {
            api = null;
        }
        /* @ts-ignore */
        return api;
    }

    /**
     * Add baseUrl as prefix if the api url is relative
     * @param url Relative api url
     * @param service Which microservice this API belongs to
     */
    prepareUrl(url: string, service?: MicroserviceId): string {
        if (url.trim().indexOf('http') !== 0) {
            let baseUrl: string;

            if (typeof this.baseUrl === 'string') {
                baseUrl = this.baseUrl;
            } else if (service && this.baseUrl[service]) {
                baseUrl = this.baseUrl[service];
            } else {
                // Default to first available service if not specified and baseUrl is an object
                baseUrl = typeof this.baseUrl === 'object' ? Object.values(this.baseUrl)[0] || '' : '';
            }

            url = (baseUrl + url).trim();
        }
        return url;
    }

    /**
     * Get request headers for a specific service
     * @param service Which microservice
     * @returns Headers for the specified service
     */
    getRequestHeaders(service?: MicroserviceId): Record<string, string> {
        if (!this.requestHeaders) {
            return {};
        }

        // Check if requestHeaders is structured by service (has nested objects)
        const hasServiceStructure =
            typeof this.requestHeaders === 'object' &&
            Object.keys(this.requestHeaders).some(
                (key) =>
                    typeof (this.requestHeaders as Record<string, unknown>)[key] === 'object' &&
                    (this.requestHeaders as Record<string, unknown>)[key] !== null
            );

        if (hasServiceStructure) {
            // Headers are organized by service
            const serviceBasedHeaders = this.requestHeaders as Record<MicroserviceId, Record<string, string>>;
            const serviceHeaders = service ? serviceBasedHeaders[service] : Object.values(serviceBasedHeaders)[0];
            return serviceHeaders || {};
        } else {
            // Headers are flat, return as is
            return this.requestHeaders as Record<string, string>;
        }
    }
}
